import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { safeFetchJson } from "@/utils/safeFetch";

// Pages
import RoleSelection from "@/pages/RoleSelection";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import TalentDashboard from "@/pages/TalentDashboard";
import TrainerDashboard from "@/pages/TrainerDashboard";
import EnterpriseDashboard from "@/pages/EnterpriseDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ProfileCreate from "@/pages/ProfileCreate";
import ProfileView from "@/pages/ProfileView";
import BrowseTalents from "@/pages/BrowseTalents";
import AuditionRoom from "@/pages/AuditionRoom";
import MeetingRoom from "@/pages/MeetingRoom";
import CastingCall from "@/pages/CastingCall";
import Chat from "@/pages/Chat";
import LiveSchool from "@/pages/LiveSchool";
import Membership from "@/pages/Membership";
import Services from "@/pages/Services";
import GoogleCallback from "@/pages/GoogleCallback";
import AccessDenied from "@/pages/AccessDenied";
// Role-specific wizards
import TalentWizard from "@/pages/TalentWizard";
import TrainerWizard from "@/pages/TrainerWizard";
import EnterpriseWizard from "@/pages/EnterpriseWizard";
// Enterprise TalentBoard (CRM)
import TalentBoard from "@/pages/TalentBoard";
// Casting Projects
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import ClientProjectView from "@/pages/ClientProjectView";
// Static Pages
import AboutUs from "@/pages/AboutUs";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
// Public Events
import PublicEvents from "@/pages/PublicEvents";
import PublicEventPage from "@/pages/PublicEventPage";
// Marketing Funnel
import Funnel from "@/pages/Funnel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google Analytics GA4 - Page view tracking for SPA
const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Send page_view event to GA4 on route change
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href
      });
    }
  }, [location]);
};

// Global Auth Context for session persistence
const SessionContext = createContext(null);

export const useSession = () => useContext(SessionContext);

// Session Provider - checks for existing session on app load
const SessionProvider = ({ children }) => {
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const checkPerformed = useRef(false);

  useEffect(() => {
    if (checkPerformed.current) return;
    checkPerformed.current = true;

    const checkExistingSession = async () => {
      try {
        // Check if we have a valid session cookie (auto-login)
        // Use safeFetchJson which handles response body reading safely
        const parsed = await safeFetchJson(`${API_URL}/api/auth/session/check`, {
          method: 'GET',
          credentials: 'include'
        });

        if (parsed.ok && parsed.data) {
          const data = parsed.data;
          if (data.authenticated && data.user) {
            // Auto-login: restore session from cookie
            localStorage.setItem('token', data.session_token);
            localStorage.setItem('user', JSON.stringify(data.user));
            setSessionUser(data.user);
          }
        }
      } catch (error) {
        console.log('Session check failed:', error);
      } finally {
        setSessionChecked(true);
      }
    };

    checkExistingSession();
  }, []);

  if (!sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider value={{ sessionUser, setSessionUser }}>
      {children}
    </SessionContext.Provider>
  );
};

// Auth Context (legacy - kept for compatibility)
export const AuthContext = ({ children }) => {
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Auth Callback Component for Google OAuth
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        // Google login = Talent only in simplified model
        const response = await fetch(`${API_URL}/api/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) throw new Error('Auth failed');
        
        const data = await response.json();
        localStorage.setItem('token', data.session_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Clear hash and navigate
        window.history.replaceState(null, '', window.location.pathname);
        
        if (data.user.role === 'admin') {
          navigate('/admin/dashboard', { state: { user: data.user } });
        } else {
          navigate('/talent/dashboard', { state: { user: data.user } });
        }
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component - supports single role or array of roles
// Single Role Model: Each user has exactly ONE role
const ProtectedRoute = ({ children, requiredRole, allowedRoles }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const { sessionUser, setSessionUser } = useSession() || {};

  useEffect(() => {
    // Always check localStorage first (most up-to-date after login/logout)
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setIsAuthenticated(true);
        // Sync sessionUser if different
        if (setSessionUser && JSON.stringify(sessionUser) !== storedUser) {
          setSessionUser(parsed);
        }
        return;
      } catch (e) {
        // Invalid stored user, fall through to check
      }
    }

    // If we have user data passed via navigation state, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // No valid stored session
    if (!storedToken) {
      setIsAuthenticated(false);
      return;
    }

    // Verify token with backend
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Only re-check on path change

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const isAdminRoute = location.pathname.startsWith('/admin');
    const loginPath = isAdminRoute ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check role access
  const roles = allowedRoles || (requiredRole ? [requiredRole] : null);
  const userRole = user?.role;
  
  console.log("[AUTH] ProtectedRoute check:", {
    path: location.pathname,
    userRole: userRole,
    requiredRoles: roles
  });
  
  if (roles && !roles.includes(userRole)) {
    const isAdminRoute = location.pathname.startsWith('/admin');
    if (isAdminRoute && userRole !== "admin") {
      return <AccessDenied />;
    }
    
    // Redirect to role-specific dashboard
    const dashboardPath = userRole === 'admin' ? '/admin/dashboard' : `/dashboard/${userRole || 'talent'}`;
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  
  // GA4 page view tracking for SPA route changes
  usePageTracking();

  // Check for session_id in hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<RoleSelection />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      
      {/* Admin Login Routes - Direct access */}
      <Route path="/admin" element={<Login />} />
      <Route path="/admin/login" element={<Login />} />
      
      {/* ============ ROLE-SPECIFIC DASHBOARDS ============ */}
      {/* Each role has its own dashboard - no cross-role access */}
      
      {/* Talent Dashboard - ONLY for talent role */}
      <Route path="/dashboard/talent" element={
        <ProtectedRoute requiredRole="talent">
          <TalentDashboard />
        </ProtectedRoute>
      } />
      
      {/* Trainer Dashboard - ONLY for trainer role */}
      <Route path="/dashboard/trainer" element={
        <ProtectedRoute requiredRole="trainer">
          <TrainerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Enterprise Dashboard - ONLY for enterprise role */}
      <Route path="/dashboard/enterprise" element={
        <ProtectedRoute requiredRole="enterprise">
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      
      {/* Enterprise TalentBoard (Light CRM) */}
      <Route path="/enterprise/talentboard" element={
        <ProtectedRoute requiredRole="enterprise">
          <TalentBoard />
        </ProtectedRoute>
      } />
      
      {/* Enterprise Casting Projects */}
      <Route path="/enterprise/projects" element={
        <ProtectedRoute requiredRole="enterprise">
          <Projects />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/projects/:projectId" element={
        <ProtectedRoute requiredRole="enterprise">
          <ProjectDetail />
        </ProtectedRoute>
      } />
      
      {/* Public Client Project View (no auth) */}
      <Route path="/share/project/:token" element={<ClientProjectView />} />
      
      {/* Legacy route redirect - redirect old /talent/dashboard to new path */}
      <Route path="/talent/dashboard" element={<Navigate to="/dashboard/talent" replace />} />
      
      {/* ============ PROFILE ROUTES ============ */}
      <Route path="/talent/profile/create" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <ProfileCreate />
        </ProtectedRoute>
      } />
      
      {/* Role-specific wizard routes */}
      <Route path="/talent/wizard" element={
        <ProtectedRoute requiredRole="talent">
          <TalentWizard />
        </ProtectedRoute>
      } />
      <Route path="/trainer/wizard" element={
        <ProtectedRoute requiredRole="trainer">
          <TrainerWizard />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/wizard" element={
        <ProtectedRoute requiredRole="enterprise">
          <EnterpriseWizard />
        </ProtectedRoute>
      } />
      
      <Route path="/talent/profile/edit" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <ProfileCreate isEdit />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Public Routes */}
      <Route path="/browse" element={<BrowseTalents />} />
      <Route path="/public/profile/:profileId" element={<ProfileView />} />
      <Route path="/profile/:profileId" element={<ProfileView />} />
      <Route path="/p/:slug" element={<ProfileView />} />
      <Route path="/t/:slug" element={<ProfileView />} />
      <Route path="/trainer/:slug" element={<ProfileView />} />
      <Route path="/enterprise/:slug" element={<ProfileView />} />
      <Route path="/services" element={<Services />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      
      {/* Services - Protected for all logged-in users */}
      <Route path="/audition-room" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <AuditionRoom />
        </ProtectedRoute>
      } />
      <Route path="/meeting-room" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <MeetingRoom />
        </ProtectedRoute>
      } />
      <Route path="/casting-call" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <CastingCall />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/live-school" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <LiveSchool />
        </ProtectedRoute>
      } />
      <Route path="/membership" element={
        <ProtectedRoute allowedRoles={["talent", "trainer", "enterprise"]}>
          <Membership />
        </ProtectedRoute>
      } />
      
      {/* Static Pages - Public */}
      <Route path="/about" element={<AboutUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Public Events */}
      <Route path="/events" element={<PublicEvents />} />
      <Route path="/events/public/:eventId" element={<PublicEventPage />} />
      
      {/* Marketing Funnel - Standalone public page */}
      <Route path="/funnel" element={<Funnel />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Build tag for verification
  const BUILD_TAG = "2026-02-05-18:30";
  
  return (
    <div className="App">
      <BrowserRouter>
        <SessionProvider>
          <AppRouter />
          <Toaster position="top-right" />
          {/* Build tag - visible on all pages */}
          <div className="fixed bottom-1 right-1 text-[10px] text-gray-300 pointer-events-none z-50">
            Build: {BUILD_TAG}
          </div>
        </SessionProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
